<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Modal from '@/components/ui/Modal.vue'
import Button from '@/components/ui/Button.vue'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { usePlanLimit } from '@/composables/usePlanLimit'
import { usePortalSubscriptionUrl } from '@/composables/usePortalSubscriptionUrl'
import { normalizePlanLimitType } from '@/utils/planLimit'

const { planLimitOpen, planLimitPayload, close } = usePlanLimit()
const { subscriptionUrl, ensurePortalUrls } = usePortalSubscriptionUrl()
const { t } = useI18n()

const limitType = computed(() =>
  normalizePlanLimitType(planLimitPayload.value?.limit),
)

const title = computed(() => t(`planLimit.${limitType.value}.title`))

const body = computed(() => t(`planLimit.${limitType.value}.body`))

const usageLabel = computed(() => {
  const payload = planLimitPayload.value
  if (!payload) return ''
  if (limitType.value === 'storage') {
    return t('planLimit.usageStorage', { used: payload.used, max: payload.max })
  }
  return t('planLimit.usageCount', { used: payload.used, max: payload.max })
})

watch(planLimitOpen, (open) => {
  if (open) void ensurePortalUrls()
})

const handleClose = () => {
  close()
}
</script>

<template>
  <Modal
    :open="planLimitOpen"
    :title="title"
    size="sm"
    @close="handleClose"
  >
    <div class="sm:flex sm:items-start">
      <div
        class="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10 dark:bg-red-900/30"
      >
        <ExclamationTriangleIcon
          class="h-6 w-6 text-red-600 dark:text-red-400"
          aria-hidden="true"
        />
      </div>
      <div class="mt-3 text-center sm:mt-0 sm:ms-4 sm:text-start">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          {{ body }}
        </p>
        <p
          v-if="planLimitPayload"
          class="mt-2 text-sm font-medium tabular-nums text-body"
        >
          {{ usageLabel }}
        </p>
        <p class="mt-2 text-sm text-red-600 dark:text-red-400">
          {{ t('planLimit.limitReachedHint') }}
        </p>
      </div>
    </div>

    <template #footer>
      <div class="flex w-full flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button
          variant="secondary"
          @click="handleClose"
        >
          {{ t('planLimit.close') }}
        </Button>
        <a
          v-if="subscriptionUrl"
          :href="subscriptionUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex justify-center rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:bg-primary-500 dark:hover:bg-primary-600"
        >
          {{ t('planLimit.upgradePlan') }}
        </a>
      </div>
    </template>
  </Modal>
</template>
